// TAS-358: React.lazy with one reload on a chunk-load failure.
//
// After a deploy, a tab that still holds the old index.html asks for chunk
// hashes that no longer exist. The request 404s (or the host answers with the
// SPA's index.html as text/html) and the dynamic import rejects. React.lazy
// then throws to the nearest error boundary, and on a route the player did not
// choose (the scoreboard step arrives by broadcast) that reads as a blank or
// broken screen. A reload fetches the current index.html and the right chunks.
//
// The reload is guarded in sessionStorage so a chunk that fails for any other
// reason cannot reload forever: one reload per RELOAD_COOLDOWN_MS, then the
// error is rethrown and the boundary shows it. Anything that is not a chunk
// load failure is rethrown straight away.
import { lazy } from 'react';

export const CHUNK_RELOAD_KEY = 'tasted:chunk-reloaded-at';
export const RELOAD_COOLDOWN_MS = 10 * 60 * 1000;

const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i, // Chrome
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /loading (css )?chunk [\w-]+ failed/i, // webpack-style, kept for safety
  /chunkloaderror/i,
  /unexpected token '<'/i, // chunk served as HTML, parsed as JS
  /expected a javascript( or wasm)? module script/i, // strict MIME check
  /mime type of "text\/html"/i,
  // WebKit (iPhone Safari, Brave iOS) on a chunk served as HTML — the one
  // wording this list lacked; game 3241 step 7 hit the error boundary on it
  // (2026-09-23) after three staging deploys landed mid-game.
  /is not a valid javascript mime type/i,
];

export function isChunkLoadError(err) {
  if (!err) return false;
  const text = `${err.name || ''} ${err.message || ''}`;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

function defaultStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

// The cooldown is per CHUNK, not per tab. One reload per ten minutes was
// meant to stop a reload loop, but three deploys in half an hour (2026-09-23,
// game 3241) meant the second stale chunk in that window was rethrown and
// the player sat on the error card with buttons that could not help. A
// DIFFERENT missing chunk is a new deploy, not a loop; only the SAME chunk
// failing again after a reload is.
function chunkKey(err) {
  const text = `${err?.message || ''}`;
  const m = text.match(/\/assets\/[\w.-]+\.(?:m?js|css)/i);
  return m ? m[0] : text.slice(0, 200);
}

function readReloaded(storage) {
  try {
    const raw = storage?.getItem(CHUNK_RELOAD_KEY);
    if (!raw) return null;
    // Older writes were a bare timestamp; still honour them as "any chunk".
    if (/^\d+$/.test(raw)) return { at: Number(raw), key: null };
    const parsed = JSON.parse(raw);
    return Number.isFinite(parsed?.at) ? parsed : null;
  } catch {
    return null;
  }
}

function writeReloaded(storage, at, key) {
  try {
    storage?.setItem(CHUNK_RELOAD_KEY, JSON.stringify({ at, key }));
  } catch {
    // Private mode or a full store: no guard means no reload, which is the safe side.
    return false;
  }
  return true;
}

/**
 * Run a dynamic import. On a chunk-load failure, reload the page once (per
 * cooldown window) and never resolve, since the page is going away. On a
 * repeat failure inside the window, or any other error, rethrow.
 */
export async function importWithReload(importer, { storage, reload, now } = {}) {
  const store = storage === undefined ? defaultStorage() : storage;
  const doReload = reload || (() => window.location.reload());
  const clock = now || Date.now;
  try {
    return await importer();
  } catch (err) {
    if (!isChunkLoadError(err)) throw err;
    const last = readReloaded(store);
    const current = clock();
    const key = chunkKey(err);
    const sameChunk = last !== null && (last.key === null || last.key === key);
    if (sameChunk && current - last.at < RELOAD_COOLDOWN_MS) throw err;
    if (!writeReloaded(store, current, key)) throw err;
    doReload();
    return new Promise(() => {});
  }
}

export function lazyWithRetry(importer) {
  return lazy(() => importWithReload(importer));
}
